import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  TextField,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import {
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  X,
  ArrowLeft,
  ArrowRight,
} from 'lucide-react';
import { useAuth } from '../../../../AuthProvider';

const ImportWizard = ({ onComplete, onCancel }) => {
  const { getAccessToken } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [fileName, setFileName] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [columnMappings, setColumnMappings] = useState({});
  const [fileColumns, setFileColumns] = useState([]);
  const [validationIssues, setValidationIssues] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [parsedFileData, setParsedFileData] = useState([]);

  const steps = [
    { label: 'Upload File', number: 1 },
    { label: 'Map Columns', number: 2 },
    { label: 'Validate Data', number: 3 },
    { label: 'Preview & Confirm', number: 4 },
  ];

  const requiredFields = [
    { key: 'buildingAddress', label: 'Building Address', aliases: ['buildingAddress', 'address', 'building address', 'property address', 'location'] },
    { key: 'totalUnits', label: 'Total Units', aliases: ['totalUnits', 'total units', 'units', 'unit count', 'total_unit_count'] },
    { key: 'unitNumber', label: 'Unit Number', aliases: ['unitNumber', 'unit number', 'unit', 'unit #', 'unit_num', 'apt', 'apartment'] },
    { key: 'bedrooms', label: 'Bedrooms', aliases: ['bedrooms', 'bedroom', 'bed', 'br', 'beds', 'bedrooms_count'] },
    { key: 'bathrooms', label: 'Bathrooms', aliases: ['bathrooms', 'bathroom', 'bath', 'ba', 'baths', 'bathrooms_count'] },
    { key: 'sqft', label: 'Square Feet', aliases: ['sqft', 'sq ft', 'square feet', 'square_feet', 'area', 'size', 'square footage'] },
    { key: 'currentRent', label: 'Current Rent', aliases: ['currentRent', 'current rent', 'rent', 'price', 'monthly rent', 'rent_amount', 'rental price'] },
  ];

  const optionalFields = [
    { key: 'buildingName', label: 'Building Name', aliases: ['buildingName', 'building name', 'name', 'property name', 'complex name'] },
    { key: 'yearBuilt', label: 'Year Built', aliases: ['yearBuilt', 'year built', 'year', 'built', 'construction year', 'year_built'] },
    { key: 'amenities', label: 'Amenities', aliases: ['amenities', 'amenity', 'features', 'unit amenities'] },
  ];

  const findBestMatch = (targetKey, aliases, fileColumns) => {
    if (!fileColumns || fileColumns.length === 0) return null;
    
    const allAliases = [targetKey, ...aliases];
    
    for (const column of fileColumns) {
      if (!column) continue;
      const columnLower = column.toLowerCase().trim();
      
      for (const alias of allAliases) {
        const aliasLower = alias.toLowerCase().trim();
        
        if (columnLower === aliasLower) {
          return column;
        }
        
        const columnNormalized = columnLower.replace(/[^a-z0-9]/g, '');
        const aliasNormalized = aliasLower.replace(/[^a-z0-9]/g, '');
        
        if (columnNormalized === aliasNormalized && columnNormalized.length > 0) {
          return column;
        }
      }
    }
    
    for (const column of fileColumns) {
      if (!column) continue;
      const columnLower = column.toLowerCase().trim();
      
      for (const alias of allAliases) {
        const aliasLower = alias.toLowerCase().trim();
        const columnNormalized = columnLower.replace(/[^a-z0-9]/g, '');
        const aliasNormalized = aliasLower.replace(/[^a-z0-9]/g, '');
        
        if (columnNormalized.includes(aliasNormalized) || aliasNormalized.includes(columnNormalized)) {
          if (aliasNormalized.length >= 3) {
            return column;
          }
        }
      }
    }
    
    return null;
  };

  const autoMapColumns = (fileColumns) => {
    const mappings = {};
    
    if (!fileColumns || fileColumns.length === 0) {
      return mappings;
    }
    
    requiredFields.forEach(field => {
      const match = findBestMatch(field.key, field.aliases, fileColumns);
      if (match) {
        mappings[field.key] = match;
      }
    });
    
    optionalFields.forEach(field => {
      const match = findBestMatch(field.key, field.aliases, fileColumns);
      if (match) {
        mappings[field.key] = match;
      }
    });
    
    return mappings;
  };

  const parseFileForPreview = async (file, mappings) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      
      reader.onload = async (e) => {
        try {
          let rows = [];
          
          if (fileExtension === '.csv') {
            const text = e.target.result;
            const lines = text.split('\n').filter(line => line.trim());
            if (lines.length > 0) {
              const firstLine = lines[0];
              const headers = [];
              let currentHeader = '';
              let inQuotes = false;
              
              for (let i = 0; i < firstLine.length; i++) {
                const char = firstLine[i];
                if (char === '"') {
                  inQuotes = !inQuotes;
                } else if (char === ',' && !inQuotes) {
                  headers.push(currentHeader.trim().replace(/^"|"$/g, ''));
                  currentHeader = '';
                } else {
                  currentHeader += char;
                }
              }
              if (currentHeader.trim()) {
                headers.push(currentHeader.trim().replace(/^"|"$/g, ''));
              }
              
              for (let i = 1; i < lines.length; i++) {
                const line = lines[i];
                if (!line.trim()) continue;
                
                const values = [];
                let currentValue = '';
                let inQuotes = false;
                
                for (let j = 0; j < line.length; j++) {
                  const char = line[j];
                  if (char === '"') {
                    inQuotes = !inQuotes;
                  } else if (char === ',' && !inQuotes) {
                    values.push(currentValue.trim().replace(/^"|"$/g, ''));
                    currentValue = '';
                  } else {
                    currentValue += char;
                  }
                }
                values.push(currentValue.trim().replace(/^"|"$/g, ''));
                
                const row = {};
                headers.forEach((header, index) => {
                  row[header] = values[index] || '';
                });
                rows.push(row);
              }
            }
          } else if (fileExtension === '.xlsx' || fileExtension === '.xls') {
            import('xlsx').then(XLSX => {
              const workbook = XLSX.read(e.target.result, { type: 'binary' });
              const sheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[sheetName];
              rows = XLSX.utils.sheet_to_json(worksheet);
              
              const mappedRows = rows.map(row => {
                const mappedRow = {};
                Object.keys(mappings).forEach(targetField => {
                  const sourceColumn = mappings[targetField];
                  if (sourceColumn && row[sourceColumn] !== undefined) {
                    mappedRow[targetField] = row[sourceColumn];
                  }
                });
                return mappedRow;
              });
              
              resolve(mappedRows);
            }).catch(reject);
            return;
          }
          
          const mappedRows = rows.map(row => {
            const mappedRow = {};
            Object.keys(mappings).forEach(targetField => {
              const sourceColumn = mappings[targetField];
              if (sourceColumn && row[sourceColumn] !== undefined) {
                mappedRow[targetField] = row[sourceColumn];
              }
            });
            return mappedRow;
          });
          
          resolve(mappedRows);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      
      if (fileExtension === '.csv') {
        reader.readAsText(file);
      } else {
        reader.readAsBinaryString(file);
      }
    });
  };

  const parseFileForColumns = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      
      reader.onload = async (e) => {
        try {
          if (fileExtension === '.csv') {
            const text = e.target.result;
            const lines = text.split('\n').filter(line => line.trim());
            if (lines.length > 0) {
              const firstLine = lines[0];
              const headers = [];
              let currentHeader = '';
              let inQuotes = false;
              
              for (let i = 0; i < firstLine.length; i++) {
                const char = firstLine[i];
                if (char === '"') {
                  inQuotes = !inQuotes;
                } else if (char === ',' && !inQuotes) {
                  headers.push(currentHeader.trim().replace(/^"|"$/g, ''));
                  currentHeader = '';
                } else {
                  currentHeader += char;
                }
              }
              if (currentHeader.trim()) {
                headers.push(currentHeader.trim().replace(/^"|"$/g, ''));
              }
              resolve(headers);
            } else {
              reject(new Error('File is empty'));
            }
          } else if (fileExtension === '.xlsx' || fileExtension === '.xls') {
            import('xlsx').then(XLSX => {
              const workbook = XLSX.read(e.target.result, { type: 'binary' });
              const sheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[sheetName];
              const headers = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0] || [];
              resolve(headers);
            }).catch(reject);
            return;
          } else {
            reject(new Error('Unsupported file type'));
          }
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      
      if (fileExtension === '.csv') {
        reader.readAsText(file);
      } else {
        reader.readAsBinaryString(file);
      }
    });
  };

  const handleFileUpload = async e => {
    const file = e.target.files?.[0];
    if (file) {
      const validExtensions = ['.csv', '.xlsx', '.xls'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      
      if (!validExtensions.includes(fileExtension)) {
        setImportError('Invalid file type. Please upload a CSV or Excel file.');
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        setImportError('File size exceeds 10MB limit.');
        return;
      }

      setSelectedFile(file);
      setFileName(file.name);
      setImportError(null);
      
      try {
        setUploadProgress(50);
        const columns = await parseFileForColumns(file);
        console.log('Parsed columns:', columns);
        setFileColumns(columns);
        
        const autoMappings = autoMapColumns(columns);
        console.log('Auto-mapped columns:', autoMappings);
        setColumnMappings(autoMappings);
        
        setUploadProgress(100);
        setTimeout(() => setCurrentStep(1), 500);
      } catch (error) {
        console.error('Error parsing file:', error);
        setImportError('Failed to parse file. Please check the file format.');
        setUploadProgress(0);
      }
    }
  };

  const handleValidate = async (addUnitsToExisting = false) => {
    if (!selectedFile) {
      setImportError('Please select a file first');
      return;
    }

    const requiredMappings = requiredFields.map(f => f.key);
    const missingMappings = requiredMappings.filter(key => !columnMappings[key] || columnMappings[key] === '');
    
    if (missingMappings.length > 0) {
      setImportError(`Please map all required fields: ${missingMappings.join(', ')}`);
      return;
    }

    setImporting(true);
    setImportError(null);
    setValidationIssues([]);
    if (!addUnitsToExisting) {
      setImportResults(null);
    }

    try {
      const accessToken = getAccessToken();
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('columnMappings', JSON.stringify(columnMappings));
      formData.append('addUnitsToExisting', addUnitsToExisting.toString());

      const response = await fetch(
        `${process.env.REACT_APP_NODE_URL}/buildings/buildings/import`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        if (data.errors && Array.isArray(data.errors)) {
          setValidationIssues(
            data.errors.map(error => {
              const match = error.match(/Row (\d+): (.+)/);
              if (match) {
                return {
                  row: parseInt(match[1]),
                  field: match[2].split(':')[0],
                  issue: match[2],
                };
              }
              return { row: 0, field: 'general', issue: error };
            })
          );
          setCurrentStep(2);
        } else {
          setImportError(data.message || 'Failed to import buildings');
        }
        setImporting(false);
        return;
      }

      if (data.status) {
        setImportResults(data.data);
        const issues = [];
        if (data.data.errors && data.data.errors.length > 0) {
          issues.push(...data.data.errors.map(error => {
            const match = error.match(/Building at (.+): (.+)/);
            if (match) {
              return {
                row: 0,
                field: 'building',
                issue: error,
              };
            }
            return { row: 0, field: 'general', issue: error };
          }));
        }
        if (data.data.warnings && data.data.warnings.length > 0) {
          issues.push(...data.data.warnings.map(warning => {
            const match = warning.match(/Building at (.+): (.+)/);
            if (match) {
              return {
                row: 0,
                field: 'building',
                issue: warning,
              };
            }
            return { row: 0, field: 'general', issue: warning };
          }));
        }
        if (issues.length > 0) {
          setValidationIssues(issues);
        }
        setCurrentStep(2);
      } else {
        setImportError(data.message || 'Failed to import buildings');
      }
    } catch (error) {
      console.error('Error importing buildings:', error);
      setImportError(error.message || 'Failed to import buildings');
    } finally {
      setImporting(false);
    }
  };

  const handleNext = async () => {
    if (currentStep === 0) {
      if (selectedFile) {
        setCurrentStep(1);
      }
    } else if (currentStep === 1) {
      const requiredMappings = requiredFields.map(f => f.key);
      const missingMappings = requiredMappings.filter(key => !columnMappings[key] || columnMappings[key] === '');
      
      if (missingMappings.length > 0) {
        setImportError(`Please map all required fields: ${missingMappings.join(', ')}`);
        return;
      }
      setCurrentStep(2);
    } else if (currentStep === 2) {
      if (!importResults) {
        handleValidate();
      } else {
        const hasDuplicateWarnings = importResults.warnings && importResults.warnings.some(warning => 
          warning.includes('A building with this address already exists')
        );
        
        if (hasDuplicateWarnings && importResults.success === 0 && !importResults.unitsAdded) {
          await handleValidate(true);
          return;
        }
        
        if (importResults.success > 0 || importResults.unitsAdded > 0) {
          try {
            const previewData = await parseFileForPreview(selectedFile, columnMappings);
            setParsedFileData(previewData);
            setCurrentStep(3);
          } catch (error) {
            console.error('Error parsing file for preview:', error);
            setImportError('Failed to load preview data. Please try again.');
          }
        } else {
          setImportError('No buildings were successfully imported. Please fix the errors and try again.');
        }
      }
    } else if (currentStep === 3) {
      onComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  return (
    <Box
      sx={{
        flex: 1,
        backgroundColor: '#F9F9F9',
        padding: { xs: '1rem', sm: '1.5rem', md: '2rem' },
        overflow: 'auto',
      }}
    >
      <Box
        sx={{
          maxWidth: '1024px',
          marginX: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: '1rem', md: '1.5rem' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '1.5rem',
                fontWeight: 600,
                color: '#111827',
              }}
            >
              Import Units
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: '#6B7280',
              }}
            >
              Upload your portfolio data from CSV or Excel
            </Typography>
          </Box>
          <IconButton onClick={onCancel}>
            <X size={20} />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            overflowX: 'auto',
            paddingBottom: { xs: '0.5rem', md: 0 },
          }}
        >
          {steps.map((step, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                flex: { xs: '0 0 auto', sm: 1 },
                minWidth: { xs: '80px', sm: 'auto' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: 1,
                  minWidth: { xs: '80px', sm: 'auto' },
                }}
              >
                <Box
                  sx={{
                    width: { xs: '32px', md: '40px' },
                    height: { xs: '32px', md: '40px' },
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    backgroundColor:
                      index <= currentStep ? '#39585C' : '#F3F4F6',
                    color: index <= currentStep ? '#FFFFFF' : '#6B7280',
                    fontSize: { xs: '0.75rem', md: '1rem' },
                  }}
                >
                  {index < currentStep ? (
                    <CheckCircle2 size={16} />
                  ) : (
                    step.number
                  )}
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: '0.625rem', sm: '0.75rem', md: '0.875rem' },
                    marginTop: '0.5rem',
                    color: index <= currentStep ? '#111827' : '#6B7280',
                    fontWeight: index <= currentStep ? 500 : 400,
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {step.label}
                </Typography>
              </Box>
              {index < steps.length - 1 && (
                <Box
                  sx={{
                    height: '2px',
                    flex: 1,
                    marginX: { xs: '0.5rem', md: '1rem' },
                    backgroundColor:
                      index < currentStep ? '#39585C' : '#E5E7EB',
                    minWidth: { xs: '20px', sm: 'auto' },
                    display: { xs: 'none', sm: 'block' },
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        <Card
          sx={{
            padding: { xs: '1rem', sm: '1.5rem', md: '2rem' },
            backgroundColor: '#FFFFFF',
          }}
        >
          {currentStep === 0 && (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
            >
              <Box
                sx={{
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box
                    sx={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      backgroundColor: '#E8F0F1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Upload size={40} color='#39585C' />
                  </Box>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: '1.125rem',
                      fontWeight: 600,
                      color: '#111827',
                    }}
                  >
                    Upload your file
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      color: '#6B7280',
                    }}
                  >
                    Supports CSV and Excel (.xlsx) files
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  border: '2px dashed #E5E7EB',
                  borderRadius: '0.5rem',
                  padding: '3rem',
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: '#39585C',
                  },
                  transition: 'border-color 0.2s',
                }}
              >
                <input
                  type='file'
                  accept='.csv,.xlsx,.xls'
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                  id='file-upload'
                />
                <label
                  htmlFor='file-upload'
                  style={{ cursor: 'pointer', display: 'block' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    <FileSpreadsheet size={48} color='#6B7280' />
                    <Box>
                      <Typography
                        component='span'
                        sx={{
                          color: '#39585C',
                          fontWeight: 500,
                          fontSize: '0.875rem',
                        }}
                      >
                        Click to upload
                      </Typography>
                      <Typography
                        component='span'
                        sx={{
                          color: '#6B7280',
                          fontSize: '0.875rem',
                        }}
                      >
                        {' '}
                        or drag and drop
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        color: '#6B7280',
                      }}
                    >
                      CSV or Excel up to 10MB
                    </Typography>
                  </Box>
                </label>
              </Box>

              {fileName && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: '0.875rem',
                    }}
                  >
                    <Typography sx={{ fontWeight: 500, color: '#111827' }}>
                      {fileName}
                    </Typography>
                    <Typography sx={{ color: '#6B7280' }}>
                      {uploadProgress}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant='determinate'
                    value={uploadProgress}
                    sx={{
                      height: '8px',
                      borderRadius: '4px',
                      backgroundColor: '#E5E7EB',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#39585C',
                      },
                    }}
                  />
                </Box>
              )}

              <Box sx={{ paddingTop: '1rem' }}>
                <Button
                  variant='text'
                  onClick={async () => {
                    try {
                      const accessToken = getAccessToken();
                      const response = await fetch(
                        `${process.env.REACT_APP_NODE_URL}/buildings/buildings/template`,
                        {
                          method: 'GET',
                          headers: {
                            Authorization: `Bearer ${accessToken}`,
                          },
                        }
                      );

                      if (response.ok) {
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'buildings-import-template.csv';
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                      } else {
                        setImportError('Failed to download template');
                      }
                    } catch (error) {
                      console.error('Error downloading template:', error);
                      setImportError('Failed to download template');
                    }
                  }}
                  sx={{
                    fontSize: '0.875rem',
                    color: '#39585C',
                    textTransform: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Download CSV Template
                </Button>
              </Box>
            </Box>
          )}

          {currentStep === 1 && (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  Map Your Columns
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                  }}
                >
                  Match your file columns to our fields
                </Typography>
              </Box>

              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: '#111827',
                      marginBottom: '0.75rem',
                    }}
                  >
                    Required Fields
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.75rem',
                    }}
                  >
                    {requiredFields.map(field => (
                      <Box
                        key={field.key}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                          gap: '1rem',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          sx={{ color: '#111827', fontSize: '0.875rem' }}
                        >
                          {field.label} *
                        </Typography>
                        <FormControl fullWidth size='small'>
                          <Select
                            value={columnMappings[field.key] || ''}
                            onChange={e =>
                              setColumnMappings({
                                ...columnMappings,
                                [field.key]: e.target.value,
                              })
                            }
                          >
                            <MenuItem value=''>Select column...</MenuItem>
                            {fileColumns.map(col => (
                              <MenuItem key={col} value={col}>
                                {col}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: '#111827',
                      marginBottom: '0.75rem',
                    }}
                  >
                    Optional Fields
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.75rem',
                    }}
                  >
                    {optionalFields.map(field => (
                      <Box
                        key={field.key}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                          gap: '1rem',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          sx={{ color: '#6B7280', fontSize: '0.875rem' }}
                        >
                          {field.label}
                        </Typography>
                        <FormControl fullWidth size='small'>
                          <Select
                            value={columnMappings[field.key] || ''}
                            onChange={e =>
                              setColumnMappings({
                                ...columnMappings,
                                [field.key]: e.target.value,
                              })
                            }
                          >
                            <MenuItem value=''>Skip this field</MenuItem>
                            {fileColumns.map(col => (
                              <MenuItem key={col} value={col}>
                                {col}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {currentStep === 2 && (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  {importing ? 'Validate & Import' : importResults ? 'Import Results' : 'Validate & Import'}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                  }}
                >
                  {importing 
                    ? 'Processing your file...'
                    : importResults 
                    ? 'Review the import results below'
                    : 'Click "Validate & Import" to process your file'}
                </Typography>
              </Box>

              {importing && (
                <Box sx={{ textAlign: 'center', padding: '2rem' }}>
                  <CircularProgress />
                  <Typography sx={{ marginTop: '1rem', color: '#6B7280' }}>
                    Validating and importing data...
                  </Typography>
                </Box>
              )}

              {!importing && (
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
                >
                  {importError && (
                    <Alert severity='error' onClose={() => setImportError(null)}>
                      {importError}
                    </Alert>
                  )}

                  {!importError && !importResults && (
                    <Alert severity='info'>
                      Ready to validate and import. Click "Validate & Import" to proceed.
                    </Alert>
                  )}

                  {importResults && (importResults.success > 0 || importResults.unitsAdded > 0) && (
                    <Alert
                      severity='success'
                      icon={<CheckCircle2 size={20} />}
                      sx={{
                        backgroundColor: '#E8F0F1',
                        borderColor: '#4FA490',
                        color: '#39585C',
                      }}
                    >
                      <Typography sx={{ fontWeight: 500, color: '#39585C' }}>
                        {importResults.success > 0 && `${importResults.success} building(s) imported successfully`}
                        {importResults.success > 0 && importResults.unitsAdded > 0 && '. '}
                        {importResults.unitsAdded > 0 && `${importResults.unitsAdded} unit(s) added to ${importResults.buildingsUpdated} existing building(s)`}
                      </Typography>
                      {importResults.warnings && importResults.warnings.length > 0 && (
                        <Typography sx={{ fontSize: '0.875rem', color: '#047857', marginTop: '0.5rem' }}>
                          {importResults.warnings.length} warning(s) occurred
                        </Typography>
                      )}
                    </Alert>
                  )}

                  {importResults && importResults.warnings && importResults.warnings.some(warning => 
                    warning.includes('A building with this address already exists')
                  ) && importResults.success === 0 && (
                    <Alert
                      severity='warning'
                      icon={<AlertCircle size={20} />}
                      sx={{
                        backgroundColor: '#FEF3C7',
                        borderColor: '#FDE68A',
                      }}
                    >
                      <Typography sx={{ fontWeight: 500, color: '#92400E', marginBottom: '0.5rem' }}>
                        Duplicate buildings detected
                      </Typography>
                      <Typography sx={{ fontSize: '0.875rem', color: '#B45309' }}>
                        Some buildings in your file already exist. If you proceed, units from these buildings will be added to the existing buildings in the database.
                      </Typography>
                    </Alert>
                  )}

                  {importResults && importResults.errors && importResults.errors.length > 0 && (
                    <Alert
                      severity='warning'
                      icon={<AlertCircle size={20} />}
                    >
                      <Typography sx={{ fontWeight: 500 }}>
                        {importResults.errors.length} error(s) occurred during import
                      </Typography>
                    </Alert>
                  )}

                  {validationIssues.length > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          color: '#F59E0B',
                        }}
                      >
                        <AlertCircle size={20} />
                        <Typography
                          sx={{ fontWeight: 500, fontSize: '0.875rem' }}
                        >
                          {validationIssues.length} issue(s) found
                        </Typography>
                      </Box>
                      {validationIssues.map((issue, index) => (
                        <Alert
                          key={index}
                          severity='warning'
                          icon={<AlertCircle size={20} />}
                          sx={{
                            backgroundColor: '#FEF3C7',
                            borderColor: '#FDE68A',
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: '0.875rem',
                              color: '#92400E',
                            }}
                          >
                            {issue.row > 0 ? `Row ${issue.row}: ` : ''}{issue.field}
                          </Typography>
                          <Typography
                            sx={{ fontSize: '0.875rem', color: '#B45309' }}
                          >
                            {issue.issue}
                          </Typography>
                        </Alert>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}

          {currentStep === 3 && (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  Preview & Confirm
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                  }}
                >
                  Review your import results
                </Typography>
              </Box>

              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
              >
                {importResults && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                      },
                      gap: '1rem',
                    }}
                  >
                    <Card sx={{ padding: '1rem', backgroundColor: '#F9FAFB' }}>
                      <Typography
                        sx={{
                          fontSize: '1.5rem',
                          fontWeight: 700,
                          color: '#111827',
                        }}
                      >
                        {importResults.success}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          color: '#6B7280',
                        }}
                      >
                        Buildings Created
                      </Typography>
                    </Card>
                    {importResults.errors && importResults.errors.length > 0 && (
                      <Card sx={{ padding: '1rem', backgroundColor: '#FEE2E2' }}>
                        <Typography
                          sx={{
                            fontSize: '1.5rem',
                            fontWeight: 700,
                            color: '#991B1B',
                          }}
                        >
                          {importResults.errors.length}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: '#991B1B',
                          }}
                        >
                          Errors
                        </Typography>
                      </Card>
                    )}
                    {importResults.warnings && importResults.warnings.length > 0 && (
                      <Card sx={{ padding: '1rem', backgroundColor: '#FEF3C7' }}>
                        <Typography
                          sx={{
                            fontSize: '1.5rem',
                            fontWeight: 700,
                            color: '#92400E',
                          }}
                        >
                          {importResults.warnings.length}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: '#92400E',
                          }}
                        >
                          Warnings
                        </Typography>
                      </Card>
                    )}
                  </Box>
                )}

                <Box
                  sx={{
                    border: '1px solid #E5E7EB',
                    borderRadius: '0.5rem',
                    overflow: 'hidden',
                  }}
                >
                  <TableContainer>
                    <Table size='small'>
                      <TableHead sx={{ backgroundColor: '#F3F4F6' }}>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 500,
                              fontSize: '0.875rem',
                              color: '#111827',
                            }}
                          >
                            Building
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 500,
                              fontSize: '0.875rem',
                              color: '#111827',
                            }}
                          >
                            Unit
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 500,
                              fontSize: '0.875rem',
                              color: '#111827',
                            }}
                          >
                            Bed/Bath
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 500,
                              fontSize: '0.875rem',
                              color: '#111827',
                            }}
                          >
                            Sq Ft
                          </TableCell>
                          <TableCell
                            align='right'
                            sx={{
                              fontWeight: 500,
                              fontSize: '0.875rem',
                              color: '#111827',
                            }}
                          >
                            Rent
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {parsedFileData.length > 0 ? (
                          parsedFileData.map((row, index) => {
                            const buildingName = row.buildingName || row.buildingAddress || 'Building';
                            const unitNumber = row.unitNumber || '';
                            const bedrooms = row.bedrooms || '0';
                            const bathrooms = row.bathrooms || '1';
                            const sqft = row.sqft || '';
                            const rent = row.currentRent ? `$${parseInt(row.currentRent).toLocaleString()}` : '';
                            
                            return (
                              <TableRow key={index}>
                                <TableCell
                                  sx={{ fontSize: '0.875rem', color: '#111827' }}
                                >
                                  {buildingName}
                                </TableCell>
                                <TableCell
                                  sx={{ fontSize: '0.875rem', color: '#111827' }}
                                >
                                  {unitNumber}
                                </TableCell>
                                <TableCell
                                  sx={{ fontSize: '0.875rem', color: '#6B7280' }}
                                >
                                  {bedrooms}/{bathrooms}
                                </TableCell>
                                <TableCell
                                  sx={{ fontSize: '0.875rem', color: '#6B7280' }}
                                >
                                  {sqft ? parseInt(sqft).toLocaleString() : '-'}
                                </TableCell>
                                <TableCell
                                  align='right'
                                  sx={{
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: '#111827',
                                  }}
                                >
                                  {rent}
                                </TableCell>
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5} align='center' sx={{ padding: '2rem', color: '#6B7280' }}>
                              No data to display
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            </Box>
          )}

        </Card>

        {currentStep < 4 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Button
              variant='outlined'
              onClick={handleBack}
              disabled={currentStep === 0}
              startIcon={<ArrowLeft size={16} />}
              sx={{
                textTransform: 'none',
                borderColor: '#D1D5DB',
                color: '#374151',
              }}
            >
              Back
            </Button>
            <Button
              variant='contained'
              onClick={handleNext}
              disabled={
                importing || 
                (currentStep === 0 && !selectedFile) || 
                (currentStep === 2 && importResults && importResults.success === 0 && 
                 !importResults.unitsAdded &&
                 !(importResults.warnings && importResults.warnings.some(warning => 
                   warning.includes('A building with this address already exists')
                 )))
              }
              endIcon={currentStep !== 3 ? <ArrowRight size={16} /> : null}
              startIcon={importing ? <CircularProgress size={16} /> : null}
              sx={{
                backgroundColor: '#39585C',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#2A3F42' },
                '&:disabled': { backgroundColor: '#9CA3AF' },
              }}
            >
              {currentStep === 0
                ? 'Continue'
                : currentStep === 1
                ? 'Continue'
                : currentStep === 2
                ? importing
                  ? 'Validating...'
                  : importResults && importResults.warnings && importResults.warnings.some(warning => 
                      warning.includes('A building with this address already exists')
                    ) && importResults.success === 0 && !importResults.unitsAdded
                  ? 'Add Units to Existing Buildings'
                  : importResults && (importResults.success > 0 || importResults.unitsAdded > 0)
                  ? 'Continue to Preview'
                  : importResults
                  ? 'Continue to Preview'
                  : 'Validate & Import'
                : currentStep === 3
                ? 'Done'
                : 'Continue'}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ImportWizard;
